// ═══════════════════════════════════════════════════════════════════
//  modules/hub/Hub.js
//  Module « Hub » — le troisième serveur, celui que l'intranet ignorait.
//
//  Réservé aux fondateurs. L'interface le cache aux autres, le backend
//  le refuse : la première est une politesse, le second est la serrure.
//
//  ── Ce que l'onglet Permissions n'est PAS ─────────────────────────
//  Ce n'est pas une seconde matrice. Le LuckPerms du Hub est en
//  `storage-method: mysql`, `server: global` — identique aux deux autres.
//  Les trois serveurs partagent UNE matrice, et ce qu'on voit ici en est
//  la partie visible depuis le Hub, filtrée sur la colonne `serveurs`
//  du catalogue.
//
//  Concrètement : accorder une permission ici l'accorde partout. Elle ne
//  produit un effet que là où un plugin la lit — et `boxoria.staff`, par
//  exemple, n'est lu que par BoxoriaGuard, qui ne tourne que sur le Hub.
//
//  C'est pour ça qu'on réutilise le composant LuckPerms tel quel plutôt
//  que d'en écrire un second : mêmes garde-fous, même journal, même
//  chemin d'écriture. Deux chemins vers la même table, c'est deux jeux
//  de règles à maintenir, et un jour un seul des deux.
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from "react";
import { THEME } from "../../socle/theme";
import { LuckPerms } from "../luckperms/LuckPerms";
import { hubApi } from "./api";
import { HubConsole } from "./HubConsole";
import { HubSuivi } from "./HubSuivi";

export function Hub({ token, user }) {
  const api = useMemo(() => hubApi(token), [token]);
  const [onglet, setOnglet] = useState("suivi");
  // Par defaut, SEULES les permissions propres au Hub. Un node declare
  // par BoxoriaGuard ou ItemJoin n'existe nulle part ailleurs : pour
  // celui-la, « matrice dediee » n'est pas une facon de parler. Les
  // nodes partages (essentials.*, luckperms.*) ne sont pas caches, ils
  // sont ailleurs — dans l'onglet LuckPerms, qui est leur place.
  const [exclusif, setExclusif] = useState(true);
  const [etat, setEtat] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(() => {
    setChargement(true);
    api.etat()
      .then((d) => { setEtat(d); setErreur(null); })
      .catch((e) => setErreur(e.message))
      .finally(() => setChargement(false));
  }, [api]);

  useEffect(() => { charger(); }, [charger]);

  // Le backend refuse déjà tout ce qui n'est pas super_admin. Ce garde-ci
  // n'est là que pour ne pas afficher une page vide et ses erreurs 403.
  if (!user || user.role !== "super_admin") {
    return (
      <div style={{ color: THEME.textMuted, fontSize: 14, padding: 20 }}>
        Ce module est réservé aux fondateurs.
      </div>
    );
  }

  const btn = {
    background: THEME.surface, color: THEME.textMuted, border: `1px solid ${THEME.border}`,
    borderRadius: 9, padding: "7px 16px", fontSize: 13, cursor: "pointer",
  };
  const btnOn = { ...btn, background: THEME.accent, color: "#fff", borderColor: THEME.accent, fontWeight: 600 };

  return (
    <div>
      <div style={{ marginBottom: 6 }}>
        <h1 style={{ margin: 0, color: THEME.text, fontSize: 24, fontWeight: 650 }}>
          🌐 Hub
        </h1>
        <div style={{ color: THEME.textMuted, fontSize: 13, marginTop: 6, lineHeight: 1.55, maxWidth: "72ch" }}>
          Le troisième serveur du réseau. Sur le Hub, seules les actions d'administration sont
          permises — le grade <b>administrateur</b> est le plancher. La boussole, elle, reste
          ouverte à tous : elle ne dépend d'aucune permission.
        </div>
      </div>

      {erreur && (
        <div style={{
          background: THEME.redDim, border: `1px solid ${THEME.red}`, color: THEME.text,
          borderRadius: 10, padding: "10px 14px", fontSize: 13, margin: "14px 0",
        }}>{erreur}</div>
      )}

      <div style={{ display: "flex", gap: 8, margin: "18px 0 18px", flexWrap: "wrap" }}>
        <button style={onglet === "suivi" ? btnOn : btn} onClick={() => setOnglet("suivi")}>Suivi</button>
        <button style={onglet === "permissions" ? btnOn : btn} onClick={() => setOnglet("permissions")}>Permissions</button>
        <button style={onglet === "console" ? btnOn : btn} onClick={() => setOnglet("console")}>Console</button>
      </div>

      {onglet === "suivi" && (
        <HubSuivi etat={etat} chargement={chargement} onRafraichir={charger} />
      )}

      {onglet === "permissions" && (
        <div>
          <div style={{
            background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 10,
            padding: "14px 16px", fontSize: 12.5, color: THEME.textMuted,
            lineHeight: 1.6, marginBottom: 16,
          }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={exclusif}
                onChange={(e) => setExclusif(e.target.checked)}
                style={{ marginTop: 3, accentColor: THEME.accent, width: 15, height: 15, flex: "none" }} />
              <span>
                <b style={{ color: THEME.text }}>Seulement les permissions propres au Hub</b>
                {etat ? (
                  <span style={{ color: THEME.textDim }}>
                    {" "}— {etat.nodes_exclusifs} sur {etat.nodes} vues ici
                  </span>
                ) : null}
                <div style={{ marginTop: 6 }}>
                  Celles que <b style={{ color: THEME.text }}>seul le Hub déclare</b> : BoxoriaGuard,
                  ItemJoin, DeluxeMenus, DecentHolograms. Aucun autre serveur ne charge ces plugins,
                  donc les accorder n'a d'effet nulle part ailleurs — c'est bien une matrice dédiée,
                  pas une façon de parler.
                </div>
                <div style={{ marginTop: 6 }}>
                  Décoche pour voir aussi les permissions <b style={{ color: THEME.text }}>communes</b> aux
                  trois serveurs — <code>essentials.*</code>, <code>luckperms.*</code>. Celles-là se
                  gèrent dans l'onglet LuckPerms, qui est leur place : les toucher ici toucherait
                  spawn et boxed du même geste.
                </div>
              </span>
            </label>

            {etat && etat.nodes === 0 && (
              <div style={{ marginTop: 12, color: THEME.amber }}>
                Le catalogue ne connaît encore <b>aucune</b> permission du Hub : la liste sera vide
                tant qu'OrialisPermDump n'aura pas parlé depuis ce serveur. Voir l'onglet Suivi.
              </div>
            )}
            {etat && etat.nodes > 0 && etat.nodes_exclusifs === 0 && exclusif && (
              <div style={{ marginTop: 12, color: THEME.amber }}>
                Aucune permission n'est <b>propre</b> au Hub : tout ce qu'il déclare, les deux autres
                serveurs le déclarent aussi. Décoche pour voir la liste complète.
              </div>
            )}
          </div>
          <LuckPerms token={token} user={user} filtreServeur="hub" filtreExclusif={exclusif} />
        </div>
      )}

      {onglet === "console" && (
        <HubConsole api={api} configure={!!(etat && etat.configure)} />
      )}
    </div>
  );
}
